import { z } from 'zod'

/**
 * THE CONSOLE'S VIEW OF A TENANT — read from Cadence, never stored.
 *
 * Per the founder's ruling of 2026-08-22: Cadence is the tenant registry. A
 * tenant comes into being there (today via `handleOnboarding` on WhatsApp
 * first contact) and the console picks it up on its next fetch. The console
 * does not create tenants and holds no tenants table.
 *
 * This is stronger than "both sides use the same UUID". Two stores agreeing by
 * convention drift the first time one is written to directly — which is
 * exactly how the console came to render one org's name over another org's
 * runs. **A console with no tenants table cannot disagree with Cadence about
 * who exists, because it has nothing to disagree with.**
 *
 * So this type is a view model with no persistence behind it. Anything the
 * console *does* own per tenant (saved views, display preferences) keys off
 * the Cadence UUID directly, with no local tenant record to join to.
 */
export const tenantStatusSchema = z.enum(['PROSPECT', 'ACTIVE', 'INACTIVE'])
export type TenantStatus = z.infer<typeof tenantStatusSchema>

export const tenantStateSchema = z.enum(['PROSPECT', 'ACTIVATED', 'CONFIGURED'])
export type TenantState = z.infer<typeof tenantStateSchema>

export const tenantSchema = z.object({
  id: z.string(),
  /** For humans only. Never a key — names are edited and duplicated. */
  displayName: z.string(),
  /** Operational: may this tenant be worked on. */
  status: tenantStatusSchema,
  /**
   * Onboarding progress.
   *
   * **Do not offer this as a filter.** Cadence confirmed on 2026-08-23 that
   * nothing anywhere writes `ACTIVATED` or `CONFIGURED` — two of the three
   * values are unreachable by any code path, so a control filtering on them
   * returns an empty list while looking like it should work. Rendering it is
   * fine; the contract now says so at the field.
   */
  state: tenantStateSchema,
  /**
   * Who created the row. `unknown` means nobody recorded it; `development` is
   * a workspace deliberately kept with curated data, distinct from a fixture
   * a script abandoned.
   */
  origin: z.enum(['customer', 'fixture', 'unknown', 'development']),
  createdAt: z.string(),
})
export type Tenant = z.infer<typeof tenantSchema>

export const tenantListResponseSchema = z.object({
  tenants: z.array(tenantSchema),
  /** How many fixture rows Cadence left out. Displayed, never inferred. */
  excludedFixtures: z.number().int().nonnegative(),
  asOf: z.string(),
})
export type TenantListResponse = z.infer<typeof tenantListResponseSchema>

/**
 * A verdict as the platform describes it — label and description served, not
 * declared here.
 *
 * `value` is the CONSOLE's word (`mismatch`), translated at the one boundary
 * like everything else, so a component can key off the same vocabulary it
 * already uses without knowing the wire spelling.
 */
export const verdictReferenceSchema = z.object({
  value: z.string(),
  label: z.string(),
  description: z.string(),
})
export type VerdictReference = z.infer<typeof verdictReferenceSchema>

export const verdictsResponseSchema = z.object({
  verdicts: z.array(verdictReferenceSchema),
  asOf: z.string(),
})
export type VerdictsResponse = z.infer<typeof verdictsResponseSchema>
